import React, { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, CopyX, Download, FileText, RotateCcw, Upload } from "lucide-react";
import { toast } from "sonner";

import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Navbar from "../components/shared/Navbar";
import Sidebar from "../components/shared/Sidebar";
import HelpTooltip from "../components/shared/HelpTooltip";

const EXPECTED_COLUMNS_LABEL = "Nome, Telefone, E-mail e Valor";

function buildErrorSummary(errors = []) {
  if (!Array.isArray(errors) || errors.length === 0) return null;
  const grouped = errors.reduce((acc, error) => {
    const key = `${error.field || "generic"}::${error.reason || ""}`;
    acc[key] = acc[key] || [];
    acc[key].push(error);
    return acc;
  }, {});
  const groups = Object.values(grouped);

  if (groups.length === 1 && groups[0].length > 1) {
    const sample = groups[0][0];
    if (sample?.field === "name") {
      return {
        critical: `Erro Critico: Nao foi possivel encontrar a coluna de Nome em nenhuma das ${groups[0].length} linhas.`,
        details: [],
      };
    }
    return {
      critical: `Erro Critico: ${sample.reason} em ${groups[0].length} linhas.`,
      details: [],
    };
  }

  return {
    critical: null,
    details: errors,
  };
}

export default function Migration() {
  const [file, setFile] = useState(null);
  const [pipelines, setPipelines] = useState([]);
  const [pipelineId, setPipelineId] = useState("");
  const [columns, setColumns] = useState([]);
  const [columnId, setColumnId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/pipelines").then((response) => {
      const items = Array.isArray(response.data) ? response.data : [];
      setPipelines(items);
      const selected = items.find((pipeline) => pipeline.is_default)?.id || items[0]?.id || "";
      setPipelineId(selected);
    }).catch(() => setPipelines([]));
  }, []);

  useEffect(() => {
    const params = pipelineId ? `?pipeline_id=${encodeURIComponent(pipelineId)}` : "";
    api.get(`/columns${params}`).then((response) => {
      const seen = new Set();
      const items = (Array.isArray(response.data) ? response.data : []).filter((column) => {
        if (!column?.id || seen.has(column.id)) return false;
        seen.add(column.id);
        return true;
      });
      setColumns(items);
      setColumnId((current) => items.some((column) => column.id === current) ? current : (items[0]?.id || ""));
    }).catch(() => {
      setColumns([]);
      setColumnId("");
    });
  }, [pipelineId]);

  const downloadSampleCsv = () => {
    const csv = [
      "name;phone;email;channel;source_tag;value",
      "Maria Silva;5511999999999;maria@email.com;manual;Importacao;1500",
      "Joao Souza;5511988888888;joao@email.com;whatsapp;Campanha Abril;990",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "modelo-importacao-leads.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const resetImport = () => {
    setFile(null);
    setResult(null);
  };

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    setResult(null);

    const formData = new FormData();
    formData.append("file", file);
    if (columnId) formData.append("column_id", columnId);

    try {
      const { data } = await api.post("/migration/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(
        data.duplicates
          ? `${data.inserted} leads importados, ${data.duplicates} pulados (telefone duplicado)`
          : `${data.inserted} leads importados`
      );
    } catch {
      toast.error("Erro ao importar");
    }

    setBusy(false);
  };

  const errorSummary = buildErrorSummary(result?.errors || []);

  return (
    <div className="flex h-screen overflow-hidden bg-slate-950">
      <Sidebar />
      <main className="flex min-w-0 flex-1 flex-col overflow-y-auto">
        <Navbar title="Importar Leads" subtitle="CSV ou JSON - mapeamento automatico de campos" />

        <div className="max-w-3xl px-6 py-6">
          <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6">
            <div className="mb-5 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#25D366]/15">
                <Upload className="h-5 w-5 text-[#25D366]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-50" style={{ fontFamily: "Outfit" }}>
                  Migrador de Dados
                </h3>
                <p className="text-xs text-slate-400">
                  Aceita CSV com colunas: name, phone, email, channel, source_tag, value
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                {pipelines.length > 1 && (
                  <div className="mb-4">
                    <Label className="text-xs uppercase tracking-wider text-slate-400">Funil</Label>
                    <Select value={pipelineId} onValueChange={setPipelineId}>
                      <SelectTrigger className="mt-1.5 border-slate-700 bg-slate-800" data-testid="migration-pipeline">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                        {pipelines.map((pipeline) => (
                          <SelectItem key={pipeline.id} value={pipeline.id}>
                            {pipeline.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Label className="text-xs uppercase tracking-wider text-slate-400">Coluna de destino</Label>
                <Select value={columnId} onValueChange={setColumnId}>
                  <SelectTrigger className="mt-1.5 border-slate-700 bg-slate-800" data-testid="migration-column">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                    {columns.map((column) => (
                      <SelectItem key={column.id} value={column.id}>
                        {column.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-400">
                  Arquivo (.csv ou .json)
                  <HelpTooltip text="Linhas com o mesmo telefone de um lead que já existe na base são puladas automaticamente -- o lead existente não é sobrescrito nem atualizado, pra não perder etapa, campos personalizados ou histórico dele. A comparação ignora formatação (com/sem código de país, espaços, traços). O resumo ao final mostra quantas linhas foram puladas por isso." />
                </Label>
                <label className="mt-1.5 block cursor-pointer">
                  <input
                    data-testid="migration-file"
                    type="file"
                    accept=".csv,.json"
                    onChange={(event) => setFile(event.target.files?.[0] || null)}
                    className="hidden"
                  />
                  <div className="rounded-xl border-2 border-dashed border-slate-700 p-8 text-center transition hover:border-[#25D366]/60">
                    <FileText className="mx-auto mb-2 h-10 w-10 text-slate-500" />
                    {file ? (
                      <div className="text-sm font-semibold text-slate-200">{file.name}</div>
                    ) : (
                      <>
                        <div className="text-sm font-semibold text-slate-300">Clique para escolher um arquivo</div>
                        <div className="mt-1 text-xs text-slate-500">ou arraste para ca</div>
                      </>
                    )}
                  </div>
                </label>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  data-testid="migration-submit"
                  onClick={submit}
                  disabled={!file || busy}
                  className="bg-[#25D366] font-semibold text-slate-950 hover:bg-[#1EAC52]"
                >
                  {busy ? "Carregando..." : "Importar"}
                </Button>

                {(file || result) && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetImport}
                    disabled={busy}
                    className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
                  >
                    <RotateCcw className="mr-1.5 h-4 w-4" /> Nova Importacao
                  </Button>
                )}

                <Button
                  type="button"
                  variant="outline"
                  onClick={downloadSampleCsv}
                  disabled={busy}
                  className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
                >
                  <Download className="mr-1.5 h-4 w-4" /> Baixar CSV Modelo
                </Button>
              </div>

              {result && (
                <>
                  <div className="flex items-center gap-3 rounded-lg border border-[#25D366]/30 bg-[#25D366]/10 p-4" data-testid="migration-result">
                    <CheckCircle2 className="h-5 w-5 text-[#25D366]" />
                    <div className="text-sm text-slate-200">
                      <span className="font-bold text-[#25D366]">{result.inserted}</span> de {result.total} leads importados com sucesso.
                      {result.duplicates > 0 && (
                        <>
                          {" "}<span className="font-bold text-sky-400">{result.duplicates}</span> pulados por telefone duplicado.
                        </>
                      )}
                    </div>
                  </div>

                  {Array.isArray(result.duplicate_details) && result.duplicate_details.length > 0 && (
                    <div className="rounded-xl border border-sky-500/30 bg-sky-500/10 p-4" data-testid="migration-duplicates">
                      <div className="mb-3 flex items-center gap-2 text-sky-200">
                        <CopyX className="h-4 w-4" />
                        <h4 className="text-sm font-semibold">Linhas puladas por duplicidade</h4>
                      </div>
                      <div className="mb-3 rounded-lg border border-sky-500/20 bg-slate-950/30 px-3 py-2 text-xs text-sky-100">
                        O telefone dessas linhas já pertence a um lead existente -- nada foi alterado nesses leads, a linha só foi ignorada.
                      </div>
                      <div className="space-y-2">
                        {result.duplicate_details.map((dup, index) => (
                          <div key={`${dup.line}-${index}`} className="rounded-lg border border-sky-500/20 bg-slate-950/40 px-3 py-2 text-sm text-slate-200">
                            <span className="font-semibold text-sky-200">Linha {dup.line}:</span> {dup.reason}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {Array.isArray(result.errors) && result.errors.length > 0 && (
                    <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4" data-testid="migration-errors">
                      <div className="mb-3 flex items-center gap-2 text-amber-200">
                        <AlertTriangle className="h-4 w-4" />
                        <h4 className="text-sm font-semibold">Linhas com atencao</h4>
                      </div>

                      <div className="mb-3 rounded-lg border border-amber-500/20 bg-slate-950/30 px-3 py-2 text-xs text-amber-100">
                        Esperavamos encontrar as colunas: <span className="font-semibold">{EXPECTED_COLUMNS_LABEL}</span>.
                      </div>

                      <div className="mb-3 rounded-lg border border-amber-500/20 bg-slate-950/30 px-3 py-2 text-xs text-slate-300">
                        Dica: Certifique-se de que seu arquivo CSV utiliza virgula ou ponto e virgula para separar as colunas.
                      </div>

                      {Array.isArray(result.headers_read) && result.headers_read.length > 0 && (
                        <div className="mb-3 rounded-lg border border-amber-500/20 bg-slate-950/30 px-3 py-2 text-xs text-slate-300">
                          Colunas lidas: <span className="font-semibold text-amber-100">[{result.headers_read.join(", ")}]</span>
                        </div>
                      )}

                      {errorSummary?.critical ? (
                        <div className="rounded-lg border border-amber-400/30 bg-slate-950/40 px-3 py-3 text-sm font-semibold text-amber-100">
                          {errorSummary.critical}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {(errorSummary?.details || []).map((error, index) => (
                            <div key={`${error.line}-${index}`} className="rounded-lg border border-amber-500/20 bg-slate-950/40 px-3 py-2 text-sm text-slate-200">
                              <span className="font-semibold text-amber-200">Linha {error.line}:</span> {error.reason}
                              {error.value !== undefined && error.value !== null && error.value !== "" && (
                                <span className="text-slate-400"> — valor: {String(error.value)}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
